/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles, Tooltip} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import JobIdTitle from './JobIdTitle';
import {showPendingJobs} from './state';

export interface JobIdLinksType {
  data: GridResult;
  isPendingScript?: boolean;
  isPendingFirmware?: boolean;
  pendingJobIds: GenericObject;
  isPendingJpodFW?: boolean;
  isPendingJpodConfig?: boolean;
  isPendingHostedApp?: boolean;
  isVbuFwEnabled?: boolean;
}

const useStyles = makeStyles(() => ({
  groupSection : {
    display : 'flex'
  },
  groupSections : {
    display       : 'flex',
    alignItems    : 'center',
    letterSpacing : '0.4px'
  },
  groupName : {
    cursor         : 'pointer',
    textDecoration : 'underline'
  },
  moreGroups : {
    color : dlsColor('grey3')
  },
  descriptionIcon : {
    marginRight : '8px'
  }
}));

const groupTooltip = makeStyles(() => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'reportTooltip')
  },
  popper : {
    width         : 'auto',
    pointerEvents : 'auto'
  }
}));

/**
   * Defines the JobIdLinks component. This component is used to
   * display the pending job Id's names with links when we hover on job Id column
   *
   * ```typescript
   *
   *   import JobIdLinks from './JobIdLinks';
   *
   *   <IonGrid  columnComponents={{
   *     jobIdCell : JobIdLinks
   *   }} />
   *
   * ```
   */
export const JobIdLinks = ({
  data,
  isPendingScript= false,
  isPendingFirmware= false,
  isPendingJpodFW= false,
  isVbuFwEnabled = false,
  isPendingJpodConfig= false,
  isPendingHostedApp= false,
  pendingJobIds
}: JobIdLinksType) => {
  const dispatch                    = useDispatch();
  const classes                     = useStyles();
  const pendingJobIdLength          = pendingJobIds?.length || 0;
  const moreJobIdsText              = pendingJobIdLength > 1
    ? `${translate('admin-devices:grid.moreJobIdsText', {pendingJobIdLength: pendingJobIdLength - 1})}`
    : '';
  const [openJobId, setOpenJobId]   = useState(false);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.preventDefault();
      if (!open) {
        setOpenJobId(false);
      } else {
        setOpenJobId(true);
      }
    };
  }, [openJobId]);

  const onJobIdClick = useCallback(data => {
    setOpenJobId(false);
    const pendingJobIdUpdate = {...data, id: data.jobId};
    dispatch(showPendingJobs(pendingJobIdUpdate, true));
  }, []);

  const onFirstJobIdClick = useCallback(() => {
    onJobIdClick(data);
  }, []);

  if (data) {
    return (
      <>
        <ClickAwayListener onClickAway={toggleTooltip()}
          mountListener>
          <Tooltip arrow
            classes={groupTooltip()}
            open={openJobId}
            onClose={toggleTooltip()}
            interactive={true}
            title={
              <JobIdTitle pendingJobIds={pendingJobIds}
                onJobIdClick={onJobIdClick}
                isPendingScript={isPendingScript}
                isPendingFirmware={isPendingFirmware}
                isVbuFwEnabled={isVbuFwEnabled}
                isPendingJpodFW={isPendingJpodFW}
                isPendingJpodConfig={isPendingJpodConfig}
                isPendingHostedApp={isPendingHostedApp} />
            }
            placement='top'>
            <div className={classes.groupSection}>
              <span>{data?.newVersion ? `[${data?.newVersion}]` : ' ' }&nbsp;</span>
              <div className={classes.groupName}
                onClick={onFirstJobIdClick}>
                {data?.jobId}
              </div>
              {pendingJobIdLength > 1 && (
                <div className={classes.moreGroups}
                  onClick={toggleTooltip(true)}>
                      &nbsp;<span className={classes.groupName}>{moreJobIdsText}</span>
                </div>
              )}
            </div>
          </Tooltip>
        </ClickAwayListener>
      </>
    );
  }
  return <div/>;
};

export default JobIdLinks;
